import { Box, CircularProgress, Paper, Typography } from "@mui/material"
import { AddOutlined } from "@mui-symbols-material/w300"
import { useNavigate, useParams } from "@tanstack/react-router"
import type { FC } from "react"
import { useCallback, useEffect, useRef, useState } from "react"

import { useFinalizeCheckIn } from "./hooks/useFinalizeCheckIn"
import { canFinishCheckIn } from "./utils/validation"

import SyncWarningAlert from "@/components/common/Alerts/SyncWarningAlert"
import { Button } from "@/components/common/Button"
import SearchInput from "@/components/SearchInput"
import { PermissionActionType } from "@/constants/permissions"
import type { GoodsReceiptPallet } from "@/graphql/codegen/graphql"
import { GoodsReceiptStatus, WarehouseType } from "@/graphql/codegen/graphql"
import { BasicPathType, useAppView } from "@/hooks/useAppView"
import { usePermissionManager } from "@/hooks/usePermissionManager"
import { NotificationType } from "@/schemas/notification"
import {
  GoodsReceiptPalletsCreateDialog,
  GoodsReceiptPalletsEditableTable,
} from "@/screens/WarehouseReceiving/components"
import { GoodsReceiptPalletDeleteConfirmation } from "@/screens/WarehouseReceiving/components/GoodsReceiptPalletDeleteConfirmation"
import { GoodsReceiptPalletLpnFormDialog } from "@/screens/WarehouseReceiving/components/GoodsReceiptPalletLpnFormDialog"
import { type DestinationOverride } from "@/screens/WarehouseReceiving/components/GoodsReceiptPalletsTable"
import { GoodsReceiptPalletDestinationOverrideDialog } from "@/screens/WarehouseReceiving/components/GoodsReceiptPalletsTable/cells/GoodsReceiptPalletDestinationOverrideDialog"
import {
  GOODS_RECEIPT_PALLETS_POLL_INTERVAL,
  GOODS_RECEIPT_POLL_INTERVAL,
} from "@/screens/WarehouseReceiving/constants"
import { useGetGoodsReceipt } from "@/screens/WarehouseReceiving/hooks/useGetGoodsReceipt"
import { useGetGoodsReceiptPallets } from "@/screens/WarehouseReceiving/hooks/useGetGoodsReceiptPallets"
import { useGoodsReceiptPalletActions } from "@/screens/WarehouseReceiving/hooks/useGoodsReceiptPalletActions"
import { usePalletSearch } from "@/screens/WarehouseReceiving/hooks/usePalletSearch"
import { GoodsReceiptPalletActionType } from "@/screens/WarehouseReceiving/types"
import { isPalletAs400SyncPending } from "@/screens/WarehouseReceiving/utils"
import { useNotificationsStore } from "@/stores/useNotificationsStore"
import { classNames } from "@/utils/utils"

export const FinalizeCheckInScreen: FC = () => {
  const { isTabletView, getBasicPathFor } = useAppView()
  const { goodsReceiptId } = useParams({ strict: false })
  const navigate = useNavigate()
  const { hasPermissions } = usePermissionManager()
  const [destinationOverrides, setDestinationOverrides] = useState<Map<string, DestinationOverride>>(new Map())
  const [isInitialSyncing, setIsInitialSyncing] = useState(false)
  const [isWorkOrderSyncing, setIsWorkOrderSyncing] = useState(false)
  const [isFinalizing, setIsFinalizing] = useState(false)
  // Tracks whether Phase 2 is evaluating for the first time after the mutation succeeds.
  // On the first evaluation goodsReceipt still holds stale pre-mutation data (urql's fetching
  // state update is deferred to the next render), so the error branch must be skipped.
  const isWorkOrderSyncFirstEvalRef = useRef(false)

  const canMaintain = hasPermissions([PermissionActionType.WAREHOUSE_RECEIVING_MAINTENANCE])

  const isSyncing = isInitialSyncing || isWorkOrderSyncing

  const { goodsReceipt, isFetching: isFetchingGoodsReceipt } = useGetGoodsReceipt(goodsReceiptId, {
    isPollingEnabled: isSyncing,
    pollInterval: GOODS_RECEIPT_POLL_INTERVAL,
  })

  const {
    pallets,
    isFetching: isFetchingPallets,
    error,
    refetch,
  } = useGetGoodsReceiptPallets(goodsReceiptId, {
    isPollingEnabled: true,
    pollInterval: GOODS_RECEIPT_PALLETS_POLL_INTERVAL,
  })
  const notify = useNotificationsStore((state) => state.enqueueNotification)
  const { finalizeCheckIn } = useFinalizeCheckIn()

  const {
    actionState,
    handleCreate,
    handleDelete,
    handleDeleteConfirm,
    handleDestinationOverride,
    handleLpnEdit,
    handleQrPrint,
    handleActionSuccess,
    handleActionClose,
  } = useGoodsReceiptPalletActions({
    onRefetch: () => void refetch(),
  })

  useEffect(() => {
    setDestinationOverrides((prevOverrides) => {
      const nextOverrides = new Map(prevOverrides)
      const currentPalletIds = new Set(pallets.map((pallet) => pallet.goodsReceiptPalletId))

      // Remove overrides for deleted pallets
      for (const [palletId] of nextOverrides) {
        if (!currentPalletIds.has(palletId)) {
          nextOverrides.delete(palletId)
        }
      }

      // Add overrides for suggested destinations
      if (pallets.length) {
        pallets
          .filter((pallet) => {
            if (nextOverrides.has(pallet.goodsReceiptPalletId)) {
              return false
            }

            const suggested = pallet.suggestedDestinationWarehouseShelving
            return (
              suggested?.warehouseShelvingId &&
              suggested?.aisleCode &&
              suggested?.sectionCode &&
              suggested?.binNumber !== null &&
              suggested?.binNumber !== undefined
            )
          })
          .forEach((pallet) => {
            const suggested = pallet.suggestedDestinationWarehouseShelving!
            nextOverrides.set(pallet.goodsReceiptPalletId, {
              warehouseShelvingId: suggested.warehouseShelvingId,
              aisleCode: suggested.aisleCode,
              sectionCode: suggested.sectionCode,
              binNumber: suggested.binNumber,
              binSize: suggested.binSize ?? undefined,
            })
          })
      }

      return nextOverrides
    })
  }, [pallets])

  // Start initial sync polling if the goods receipt hasn't synced with AS400 yet
  useEffect(() => {
    if (isInitialSyncing || !goodsReceipt) {
      return
    }

    if (goodsReceipt.status === GoodsReceiptStatus.PreCheckedIn && !goodsReceipt.externalSystemId) {
      setIsInitialSyncing(true)
    }
  }, [goodsReceipt, isInitialSyncing])

  // Phase 1: Handle initial sync success or failure
  useEffect(() => {
    if (!isInitialSyncing || !goodsReceipt) return

    if (goodsReceipt.status === GoodsReceiptStatus.PreCheckedIn && goodsReceipt.externalSystemId) {
      setIsInitialSyncing(false)
      notify({
        type: NotificationType.Success,
        message: "Receipt batch has been successfully confirmed and synced with AS400.",
      })
    } else if (goodsReceipt.status !== GoodsReceiptStatus.PreCheckedIn) {
      setIsInitialSyncing(false)
      notify({
        type: NotificationType.Error,
        message: "AS400 synchronization failed. The system could not synchronize your goods receipt. Please try again.",
      })
      navigate({
        to: `/${getBasicPathFor(BasicPathType.WAREHOUSE_RECEIVING)}/soft-received/$goodsReceiptId`,
        params: { goodsReceiptId },
      })
    }
  }, [isInitialSyncing, goodsReceipt, goodsReceiptId, navigate, notify, getBasicPathFor])

  // Phase 2: Handle work order sync success or failure
  useEffect(() => {
    if (!isWorkOrderSyncing || !goodsReceipt) return

    const isFirstEval = isWorkOrderSyncFirstEvalRef.current
    isWorkOrderSyncFirstEvalRef.current = false

    const warehouseType = goodsReceipt.warehouseArea?.type
    const isNonAkrochemOrConsignment =
      warehouseType === WarehouseType.AkrochemUnmanaged || warehouseType === WarehouseType.Consignment

    if (
      goodsReceipt.status === GoodsReceiptStatus.CheckedIn &&
      (goodsReceipt.externalSystemWorkOrderId || isNonAkrochemOrConsignment)
    ) {
      setIsWorkOrderSyncing(false)
      setIsFinalizing(false)

      if (!isTabletView) {
        notify({
          type: NotificationType.Success,
          content: `Goods receipt #${goodsReceipt.agrNumber} has been successfully checked in.`,
        })
      }

      navigate({
        to: `/${getBasicPathFor(BasicPathType.WAREHOUSE_RECEIVING)}/checked-in/$goodsReceiptId/built-pallets`,
        params: { goodsReceiptId },
        search: { showSuccessDialog: isTabletView || undefined },
      })
    } else if (!isFirstEval && goodsReceipt.status !== GoodsReceiptStatus.CheckedIn) {
      setIsWorkOrderSyncing(false)
      setIsFinalizing(false)
      notify({
        type: NotificationType.Error,
        message: "Failed to create work order. Please try again.",
      })
    }
  }, [
    isWorkOrderSyncing,
    goodsReceipt,
    goodsReceiptId,
    navigate,
    notify,
    getBasicPathFor,
    isTabletView,
    setIsFinalizing,
  ])

  const handleDestinationUpdate = useCallback(
    (pallet: GoodsReceiptPallet, override: DestinationOverride) => {
      setDestinationOverrides((prevOverrides) => new Map(prevOverrides).set(pallet.goodsReceiptPalletId, override))
      handleActionSuccess()
    },
    [handleActionSuccess]
  )

  const { searchTerm, setSearchTerm, filteredPallets } = usePalletSearch(pallets)

  const isFinishEnabled = canFinishCheckIn(pallets, destinationOverrides, goodsReceipt?.warehouseArea?.type)
  const isPalletsSyncing = pallets.some(isPalletAs400SyncPending)

  const handleSaveAndExit = useCallback(() => {
    navigate({
      to: `/${getBasicPathFor(BasicPathType.WAREHOUSE_RECEIVING)}/soft-received`,
    })
  }, [navigate, getBasicPathFor])

  const handleFinish = useCallback(async () => {
    if (!isFinishEnabled) {
      notify({
        type: NotificationType.Error,
        message: "Destination required for all pallets.",
      })
      return
    }

    if (!goodsReceiptId) {
      notify({
        type: NotificationType.Error,
        message: "Invalid goods receipt ID.",
      })
      return
    }

    setIsFinalizing(true)

    try {
      const result = await finalizeCheckIn(goodsReceiptId, pallets, destinationOverrides)

      if (result.success) {
        // isFinalizing stays true until Phase 2 resolves
        isWorkOrderSyncFirstEvalRef.current = true
        setIsWorkOrderSyncing(true)
      } else {
        notify({
          type: NotificationType.Error,
          message: result.error || "Failed to finalize check-in. Please try again.",
        })
        setIsFinalizing(false)
      }
    } catch {
      notify({
        type: NotificationType.Error,
        message: "An unexpected error occurred. Please try again.",
      })
      setIsFinalizing(false)
    }
  }, [isFinishEnabled, goodsReceiptId, pallets, destinationOverrides, finalizeCheckIn, notify, setIsWorkOrderSyncing])

  if (!isSyncing && (isFetchingPallets || isFetchingGoodsReceipt) && pallets.length === 0) {
    return (
      <Box className='flex flex-1 items-center justify-center'>
        <CircularProgress />
      </Box>
    )
  }

  if (error || !pallets) {
    return (
      <Box className='p-6'>
        <Typography color='error'>Something went wrong</Typography>
      </Box>
    )
  }

  return (
    <Paper
      elevation={0}
      className={classNames("flex min-h-0 grow flex-col bg-white", {
        "rounded-lg": !isTabletView,
        "rounded-none": isTabletView,
      })}
    >
      {isInitialSyncing && (
        <Box className='mb-6'>
          <SyncWarningAlert />
        </Box>
      )}

      <Box className='mb-8'>
        <Box className='flex items-center gap-4'>
          <Typography variant='h5' component='h1' className='mr-auto text-primary'>
            Review & Finish Checking In
          </Typography>
          {canMaintain && (
            <>
              <Button
                appearance='outlined'
                onClick={handleSaveAndExit}
                disabled={isSyncing}
                data-testid='save-exit-button'
              >
                Save & Exit
              </Button>
              <Button
                appearance='contained'
                variant='primary'
                onClick={handleFinish}
                disabled={!isFinishEnabled || isFinalizing || isSyncing || isPalletsSyncing}
                data-testid='finish-check-in-button'
              >
                {isFinalizing ? <CircularProgress size={20} color='inherit' /> : "Create Work Order"}
              </Button>
            </>
          )}
        </Box>
      </Box>

      <Box className='mb-6 flex items-center justify-between gap-4'>
        <SearchInput
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          placeholder='Search by Product ID, Lot #, or Reference ID'
          className='w-full max-w-[524px]'
          data-testid='pallets-search-input'
        />
        {canMaintain && (
          <Button
            appearance='outlined'
            startIcon={<AddOutlined />}
            onClick={handleCreate}
            disabled={isSyncing}
            data-testid='add-pallets-button'
          >
            Add Pallets
          </Button>
        )}
      </Box>

      <Box className='flex flex-1 flex-col overflow-y-auto'>
        <GoodsReceiptPalletsEditableTable
          pallets={filteredPallets}
          warehouse={goodsReceipt?.warehouseArea}
          destinationOverrides={destinationOverrides}
          isSyncPending={isSyncing}
          isLoading={isFetchingPallets}
          canMaintain={canMaintain}
          onDestinationOverride={handleDestinationOverride}
          onLpnEdit={handleLpnEdit}
          onQrPrint={handleQrPrint}
          onDelete={handleDelete}
        />
      </Box>

      {goodsReceipt && actionState?.type === GoodsReceiptPalletActionType.Create && (
        <GoodsReceiptPalletsCreateDialog
          goodsReceipt={goodsReceipt}
          onClose={handleActionClose}
          onSubmit={handleActionSuccess}
        />
      )}

      {actionState?.type === GoodsReceiptPalletActionType.EditLpn && (
        <GoodsReceiptPalletLpnFormDialog
          pallet={actionState.pallet}
          onClose={handleActionClose}
          onSubmit={handleActionSuccess}
        />
      )}

      {actionState?.type === GoodsReceiptPalletActionType.Delete && (
        <GoodsReceiptPalletDeleteConfirmation onClose={handleActionClose} onConfirm={handleDeleteConfirm} />
      )}

      {goodsReceipt && actionState?.type === GoodsReceiptPalletActionType.EditDestination && (
        <GoodsReceiptPalletDestinationOverrideDialog
          pallet={actionState.pallet}
          warehouse={goodsReceipt.warehouseArea!}
          pallets={pallets}
          destinationOverrides={destinationOverrides}
          onClose={handleActionClose}
          onSubmit={(override: DestinationOverride) => handleDestinationUpdate(actionState.pallet, override)}
        />
      )}
    </Paper>
  )
}
